/**
 * Browser-side Web Push plumbing for the staff console. Safari 18.4+ exposes
 * window.pushManager (declarative web push — subscription and display with no
 * service worker); anything else falls back to registering /sw.js and using
 * the registration's pushManager. The Xano side of a subscription lives in
 * api/push.ts; this file never talks to the network itself.
 */

import { config } from "@/config/env";

/** Navigator with the Badging API, which TS's dom lib may not carry yet. */
type BadgingNavigator = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

type DeclarativeWindow = Window & { pushManager?: PushManager };

export function pushSupported(): boolean {
  return (
    Boolean(config.vapidPublicKey) &&
    "Notification" in window &&
    (Boolean((window as DeclarativeWindow).pushManager) || "serviceWorker" in navigator)
  );
}

function applicationServerKey(): ArrayBuffer {
  const b64 = config.vapidPublicKey.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
  const bin = atob(padded);
  // .buffer (not the view) — TS's PushManager typing wants a plain ArrayBuffer.
  return Uint8Array.from(bin, (c) => c.charCodeAt(0)).buffer as ArrayBuffer;
}

async function manager(): Promise<PushManager> {
  const declarative = (window as DeclarativeWindow).pushManager;
  if (declarative) return declarative;
  await navigator.serviceWorker.register("/sw.js");
  const ready = await navigator.serviceWorker.ready;
  return ready.pushManager;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    return await (await manager()).getSubscription();
  } catch {
    return null;
  }
}

/** Must be called from a user gesture — Safari refuses the permission prompt
 *  otherwise. Returns the (new or existing) subscription. */
export async function enablePush(): Promise<PushSubscription> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }
  const mgr = await manager();
  const existing = await mgr.getSubscription();
  if (existing) return existing;
  return mgr.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey() });
}

/** Unsubscribes locally and returns the endpoint so the caller can also
 *  deactivate the server row. Null if there was nothing to disable. */
export async function disablePush(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  return endpoint;
}

/** The fields Xano stores for a subscription. */
export function subscriptionFields(sub: PushSubscription): {
  endpoint: string;
  p256dh: string;
  auth_secret: string;
} {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh ?? "",
    auth_secret: json.keys?.auth ?? "",
  };
}

/** Mirror a count onto the Dock icon. No-ops when the app isn't installed or
 *  the API is missing — the in-app badges still show the same numbers. */
export async function setAppBadge(count: number): Promise<void> {
  const nav = navigator as BadgingNavigator;
  try {
    if (count > 0) await nav.setAppBadge?.(count);
    else await nav.clearAppBadge?.();
  } catch {
    /* not installed / unsupported */
  }
}
